/**
 * AssessmentClientBlock — the ONE client block for the Assessment Drafter
 * (2026-07 redesign, founder ruling): name, pronouns, and recipient
 * ("Who is this report for?") live here, at the top of the page, and the
 * WHOLE draft reads from it — the form's generate input, the pronoun
 * resolution (functional lead-in + courtesy sentence + SPICE forms), and
 * the recommendations library (which retired its duplicate name/pronoun
 * fields). One source of truth ends the she/her-report vs
 * they/them-library disagreement.
 *
 * Lookup-first with free-text fallback (both paths are real: intake
 * precedes the client record). The lookup is the shared ClientLookupField
 * (GET /v1/clients, which admits the width or the lane); callers pass
 * canReadEveryClient as canLookup so a viewer who does not read every
 * client fires NO request and simply types the name.
 *
 * State is page-owned (controlled component): the drafter page threads
 * it into the form, the picker, and the draft calls.
 *
 * The pronoun rules the pages share live here too: which client is bound
 * (picked and still named as picked), when a choice is needed before
 * Generate or before the recommendations library loads, and the key and
 * label that tell one pronoun choice from another.
 */

import { type ReactNode } from 'react';
import type { SpicePronounChoice } from '@/lib/api';
import { ClientLookupField, type ClientLookupPick } from '@/components/aiTools/ClientLookupField';
import { SelectField } from '@/components/primitives/SelectField';

export type PronounPresetChoice = '' | SpicePronounChoice['preset'];

export interface PronounCustomForms {
  subject: string;
  object: string;
  possessive_determiner: string;
  possessive_pronoun: string;
  reflexive: string;
}

export const EMPTY_PRONOUN_CUSTOM: PronounCustomForms = {
  subject: '',
  object: '',
  possessive_determiner: '',
  possessive_pronoun: '',
  reflexive: '',
};

export const PRONOUN_CUSTOM_FIELDS: Array<[keyof PronounCustomForms, string, string]> = [
  ['subject', 'Subject', 'she, he, they'],
  ['object', 'Object', 'her, him, them'],
  ['possessive_determiner', 'Possessive determiner', 'her, his, their'],
  ['possessive_pronoun', 'Possessive pronoun', 'hers, his, theirs'],
  ['reflexive', 'Reflexive', 'herself, himself, themselves'],
];

/** Where the select's value came from: the picked client's record, or the
 *  care manager's own choice. */
export type PronounSource = 'record' | 'cm';

/** The longest custom form; the server keeps forms of at most this length. */
export const PRONOUN_CUSTOM_MAX_LENGTH = 40;

/** Build the wire pronoun choice from the block's state (undefined = unset). */
export function buildPronounChoice(
  preset: PronounPresetChoice,
  custom: PronounCustomForms,
): SpicePronounChoice | undefined {
  if (preset === '') return undefined;
  if (preset === 'custom') {
    return {
      preset: 'custom',
      custom: {
        subject: custom.subject.trim(),
        object: custom.object.trim(),
        possessive_determiner: custom.possessive_determiner.trim(),
        possessive_pronoun: custom.possessive_pronoun.trim(),
        reflexive: custom.reflexive.trim(),
      },
    };
  }
  return { preset };
}

/** clients.pronouns (0128 closed set) -> the drafter's preset choice.
 *  A value outside the map (impossible today; defensive for a future
 *  vocabulary widening) reads as not recorded. */
export const STORED_PRONOUNS_TO_PRESET: Record<string, PronounPresetChoice> = {
  'she/her': 'she_her',
  'he/him': 'he_him',
  'they/them': 'they_them',
};

/** A client's recorded pronouns as the select's preset; '' when none is
 *  recorded or the value is outside the map. */
export function presetFromStored(stored: string | null): PronounPresetChoice {
  if (stored === null) return '';
  return STORED_PRONOUNS_TO_PRESET[stored] ?? '';
}

/**
 * The picked client while both names on screen still match the pick, else
 * null. A name edit after the pick unbinds the client: the subject on screen
 * is no longer that record.
 */
export function boundClient<T extends { firstName: string; lastName: string }>(
  picked: T | null,
  firstName: string,
  lastName: string,
): T | null {
  return picked !== null && picked.firstName === firstName && picked.lastName === lastName
    ? picked
    : null;
}

/** False only while Custom is chosen and any of the five forms is blank. */
export function pronounCustomComplete(
  preset: PronounPresetChoice,
  custom: PronounCustomForms,
): boolean {
  return preset !== 'custom' || PRONOUN_CUSTOM_FIELDS.every(([k]) => custom[k].trim().length > 0);
}

/**
 * True while the block cannot supply a usable choice: no choice where one is
 * required (choiceRequired), or an incomplete custom set.
 */
export function pronounsNeeded(
  preset: PronounPresetChoice,
  custom: PronounCustomForms,
  choiceRequired: boolean,
): boolean {
  return (choiceRequired && preset === '') || !pronounCustomComplete(preset, custom);
}

/**
 * A stable identity for a pronoun choice: '' for no choice; a standard preset
 * by its preset alone (any custom forms it carries are ignored); a custom set
 * by its five forms in order, a missing form counting as ''.
 */
export function pronounChoiceKey(choice: SpicePronounChoice | undefined): string {
  if (choice === undefined) return '';
  if (choice.preset !== 'custom') return JSON.stringify({ preset: choice.preset });
  const c = choice.custom ?? {};
  return JSON.stringify({
    preset: 'custom',
    custom: [
      c.subject ?? '',
      c.object ?? '',
      c.possessive_determiner ?? '',
      c.possessive_pronoun ?? '',
      c.reflexive ?? '',
    ],
  });
}

const PRESET_LABELS: Record<Exclude<SpicePronounChoice['preset'], 'custom'>, string> = {
  she_her: 'She / her',
  he_him: 'He / him',
  they_them: 'They / them',
};

/** The choice as the select names it; a custom set as "subject / object". */
export function pronounChoiceLabel(choice: SpicePronounChoice): string {
  if (choice.preset !== 'custom') return PRESET_LABELS[choice.preset];
  return `${choice.custom?.subject ?? ''} / ${choice.custom?.object ?? ''}`;
}

/** "Last, First Middle" or "First Middle Last" -> { first, last }. */
export function splitDisplayName(displayName: string): { first: string; last: string } {
  const t = displayName.trim();
  if (t.includes(',')) {
    const [last = '', rest = ''] = t.split(',', 2);
    const first = rest.trim().split(/\s+/)[0] ?? '';
    return { first, last: last.trim() };
  }
  const parts = t.split(/\s+/);
  if (parts.length === 1) return { first: parts[0] ?? '', last: '' };
  return { first: parts[0]!, last: parts.slice(1).join(' ') };
}

const INPUT_CLS =
  'mt-1.5 block w-full min-h-11 rounded-[12px] border border-sage-400/20 bg-paper px-3.5 py-2.5 text-sm text-ink placeholder:text-sage-300 outline-none transition-[border-color,box-shadow] duration-[var(--dur-base)] ease-quiet focus:border-sage-400 focus:ring-[3px] focus:ring-sage-800/[6%]';
const LABEL_CLS = 'block text-[13px] font-semibold text-sage-800';

export interface AssessmentClientBlockProps {
  tenantSlug: string;
  /** canReadEveryClient (GET /v1/clients admits the width or the lane);
   *  false = free-text only, unless a caseload is given (a line seat's own
   *  clients). */
  canLookup: boolean;
  caseload?: ReadonlyArray<ClientLookupPick>;
  firstName: string;
  lastName: string;
  recipient: string;
  pronounPreset: PronounPresetChoice;
  pronounCustom: PronounCustomForms;
  onFirstNameChange: (v: string) => void;
  onLastNameChange: (v: string) => void;
  onRecipientChange: (v: string) => void;
  /** The select's onChange passes 'cm'; a client pick passes 'record'. */
  onPronounPresetChange: (v: PronounPresetChoice, source: PronounSource) => void;
  onPronounCustomChange: (v: PronounCustomForms) => void;
  /** A lookup-picked client (id + split name) — enables filing the finished
   *  assessment to that client's record as a draft case note. Free-text
   *  entry never fires this (no id to file against). */
  onClientPicked?: (client: { id: string; firstName: string; lastName: string }) => void;
  disabled?: boolean;
  /** SPICE tweaks the recipient helper copy. */
  spice?: boolean;
  /**
   * The Assessment Letter: the recipient field becomes a multi-line block
   * (one name per line; each name gets its own TO line and the letter
   * greets them by first name).
   */
  letter?: boolean;
}

export function AssessmentClientBlock({
  tenantSlug,
  canLookup,
  caseload,
  firstName,
  lastName,
  recipient,
  pronounPreset,
  pronounCustom,
  onFirstNameChange,
  onLastNameChange,
  onRecipientChange,
  onPronounPresetChange,
  onPronounCustomChange,
  onClientPicked,
  disabled = false,
  spice = false,
  letter = false,
}: AssessmentClientBlockProps): ReactNode {
  const pickClient = (c: ClientLookupPick): void => {
    const { first, last } = splitDisplayName(c.display_name);
    onFirstNameChange(first);
    onLastNameChange(last);
    // Surface the picked client's id so the page can file the finished
    // assessment to their record (a real client, not free-text).
    onClientPicked?.({ id: c.id, firstName: first, lastName: last });
    // A pick sets the select from the client's record and clears it when none
    // is recorded; the tool then asks for a choice before Generate and Insert.
    onPronounPresetChange(presetFromStored(c.pronouns), 'record');
    onPronounCustomChange({ ...EMPTY_PRONOUN_CUSTOM });
  };

  return (
    <section
      className="rounded-card bg-paper p-6 shadow-sh2"
      data-testid="assessment-client-block"
    >
      <span className="text-[11.5px] font-semibold uppercase tracking-[0.16em] text-sage-400">
        Client
      </span>

      <ClientLookupField
        tenantSlug={tenantSlug}
        canLookup={canLookup}
        caseload={caseload}
        onPick={pickClient}
        disabled={disabled}
        inputId="assess-client-lookup"
        testIdPrefix="assessment-client"
        hint="Pick a client, or type the name below (new clients are fine)."
      />

      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label htmlFor="assess-first" className={LABEL_CLS}>
            Client first name
          </label>
          <input
            id="assess-first"
            type="text"
            value={firstName}
            onChange={(e) => onFirstNameChange(e.target.value)}
            disabled={disabled}
            className={INPUT_CLS}
          />
        </div>
        <div>
          <label htmlFor="assess-last" className={LABEL_CLS}>
            Client last name
          </label>
          <input
            id="assess-last"
            type="text"
            value={lastName}
            onChange={(e) => onLastNameChange(e.target.value)}
            disabled={disabled}
            className={INPUT_CLS}
          />
        </div>
      </div>

      <div className="mt-3" data-testid="assessment-client-pronouns">
        <label htmlFor="assess-pronouns" className={LABEL_CLS}>
          Client pronouns
        </label>
        <SelectField size="field"
          id="assess-pronouns"
          value={pronounPreset}
          onChange={(e) => onPronounPresetChange(e.target.value as PronounPresetChoice, 'cm')}
          disabled={disabled}
          className="mt-1.5 w-full"
        >
          <option value="">Select pronouns…</option>
          <option value="she_her">She / her</option>
          <option value="he_him">He / him</option>
          <option value="they_them">They / them</option>
          <option value="custom">Custom…</option>
        </SelectField>
        {pronounPreset === 'custom' && (
          <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="assessment-pronoun-custom">
            {PRONOUN_CUSTOM_FIELDS.map(([key, label, placeholder]) => (
              <div key={key}>
                <label htmlFor={`pronoun-${key}`} className="text-xs font-medium text-sage-600">
                  {label}
                </label>
                <input
                  id={`pronoun-${key}`}
                  type="text"
                  value={pronounCustom[key]}
                  maxLength={PRONOUN_CUSTOM_MAX_LENGTH}
                  onChange={(e) =>
                    onPronounCustomChange({ ...pronounCustom, [key]: e.target.value })
                  }
                  disabled={disabled}
                  placeholder={placeholder}
                  className={INPUT_CLS}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-3">
        <label htmlFor="assess-recipient" className={LABEL_CLS}>
          {letter ? 'Who is this letter for?' : 'Who is this report for?'}
        </label>
        <p className="text-xs text-ink-soft mt-0.5">
          {letter
            ? 'One name per line. Each gets its own TO line, and the letter greets them by first name.'
            : spice
              ? 'The family recipient(s). Sets the TO field and the salutation.'
              : 'The recipient on the report header (TO field).'}
        </p>
        {letter ? (
          <textarea
            id="assess-recipient"
            value={recipient}
            onChange={(e) => onRecipientChange(e.target.value)}
            disabled={disabled}
            rows={3}
            placeholder={'Edward Smith\nLinda Smith'}
            className={INPUT_CLS}
            data-testid="assessment-client-recipients"
          />
        ) : (
          <input
            id="assess-recipient"
            type="text"
            value={recipient}
            onChange={(e) => onRecipientChange(e.target.value)}
            disabled={disabled}
            placeholder={
              spice
                ? 'Family member name(s), e.g. Sarah, or Andrew and Eleanor'
                : 'Attorney, guardian, family member, trust officer, etc.'
            }
            className={INPUT_CLS}
          />
        )}
      </div>
    </section>
  );
}
